import { db } from '../config/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { farmingResultsService } from './farmingResultsService';

// Types for Lifetime Farming Results dashboard
export interface LifetimeFarmingResultsData {
  asin: string;
  companyName: string;
  roundOfFarming: number;
  farmingPeriodStart: string;
  farmingPeriodEnd: string;
  days: number;
  spend: number;
  sales: number;
  newCsts: number;
  costPerCst: number;
  comparedToTargetCst?: number; // Calculated field
}

export interface LifetimeFarmingResultsSummary {
  farmingHistory: LifetimeFarmingResultsData[];
}

// Type guards for Firestore data (new array-based structure)
interface FarmingRoundFirestoreData {
  roundOfFarming?: number | string;
  farmingPeriodStart?: string;
  farmingPeriodEnd?: string;
  days?: number | string;
  spend?: number | string;
  sales?: number | string;
  newCsts?: number | string;
  costPerCst?: number | string;
}

interface LifetimeFarmingFirestoreData {
  asin?: string;
  companyName?: string;
  farmingRounds?: FarmingRoundFirestoreData[];
  lastUpdated?: any;
  source?: string;
}

const isLifetimeFarmingData = (data: unknown): data is LifetimeFarmingFirestoreData => {
  return typeof data === 'object' && data !== null && 'farmingRounds' in data && Array.isArray((data as any).farmingRounds);
};

/**
 * Converts Excel serial date to readable date string
 */
const convertExcelDate = (serialDate: string | number): string => {
  try {
    const serial = typeof serialDate === 'string' ? parseFloat(serialDate) : serialDate;
    if (isNaN(serial)) return serialDate.toString();
    
    // Excel epoch starts from January 1, 1900 (but Excel incorrectly treats 1900 as a leap year)
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
    const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
    
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
  } catch (error) {
    console.warn('Error converting Excel date:', serialDate, error);
    return serialDate.toString();
  }
};

/**
 * Safely parse numeric values from Firestore
 */
const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

/**
 * Fetches Final $/CST from farming metrics for comparison calculation
 */
const fetchFinalCostPerCST = async (companyName: string, asin: string): Promise<number | null> => {
  try {
    const processedMetrics = await farmingResultsService.getProcessedFarmingMetrics(companyName, asin);
    if (!processedMetrics || !processedMetrics.finalCostPerCST) return null;
    
    // Extract numeric value from formatted string like "$12.50"
    const finalCostStr = processedMetrics.finalCostPerCST.replace('$', '').replace(',', '');
    const finalCost = parseFloat(finalCostStr);
    return isNaN(finalCost) ? null : finalCost;
  } catch (error) {
    console.warn(`Could not fetch Final $/CST for ${companyName}/${asin}:`, error);
    return null;
  }
};

/**
 * Fetches Lifetime Farming Results data for a specific company and ASIN
 */
export const fetchLifetimeFarmingResults = async (companyName: string, asin: string): Promise<LifetimeFarmingResultsSummary> => {
  try {
    console.log(`Fetching Lifetime Farming Results for company: ${companyName}, ASIN: ${asin}`);
    
    const farmingHistory: LifetimeFarmingResultsData[] = [];
    
    try {
      // Query all documents in the data collection for this company
      const dataCollectionRef = collection(db, 'pmt3_lifetime_farming_results', companyName, 'data');
      const querySnapshot = await getDocs(dataCollectionRef);
      
      if (querySnapshot.empty) {
        console.warn(`No Lifetime Farming Results data found for ${companyName}`);
        return {
          farmingHistory: []
        };
      }
      
      // Fetch Final $/CST once for this ASIN (used for all rows)
      const finalCostPerCST = await fetchFinalCostPerCST(companyName, asin);
      
      // Process all documents and filter for the specific ASIN
      querySnapshot.forEach(docSnap => {
        const data = docSnap.data();
        
        if (isLifetimeFarmingData(data)) {
          // Check if this document is for the requested ASIN
          const docAsin = data.asin || docSnap.id;
          
          if (docAsin === asin && data.farmingRounds) {
            // Process each farming round in the array
            data.farmingRounds.forEach(round => {
              const costPerCst = parseNumericValue(round.costPerCst);
              
              // Calculate "Compared to Target $/CST" with condition:
              // If Cost per CST is 0, then Compared to Target $/CST should also be 0
              let comparedToTargetCst: number | undefined;
              if (costPerCst === 0) {
                comparedToTargetCst = 0;
              } else if (finalCostPerCST !== null) {
                comparedToTargetCst = costPerCst - finalCostPerCST;
              } else {
                comparedToTargetCst = undefined;
              }

              const farmingData: LifetimeFarmingResultsData = {
                asin: docAsin,
                companyName: data.companyName || companyName,
                roundOfFarming: parseNumericValue(round.roundOfFarming),
                farmingPeriodStart: convertExcelDate(round.farmingPeriodStart || ''),
                farmingPeriodEnd: convertExcelDate(round.farmingPeriodEnd || ''),
                days: parseNumericValue(round.days),
                spend: parseNumericValue(round.spend),
                sales: parseNumericValue(round.sales),
                newCsts: parseNumericValue(round.newCsts),
                costPerCst: costPerCst,
                comparedToTargetCst: comparedToTargetCst
              };
              
              farmingHistory.push(farmingData);
            });
          }
        }
      });
      
      // Sort by round of farming (ascending) to show chronological order
      farmingHistory.sort((a, b) => a.roundOfFarming - b.roundOfFarming);
      
      console.log(`Found ${farmingHistory.length} farming history records for ${companyName}/${asin}`);
      
      return {
        farmingHistory
      };
      
    } catch (queryError) {
      console.error('Error querying Lifetime Farming Results:', queryError);
      return {
        farmingHistory: []
      };
    }
    
  } catch (error) {
    console.error('Error fetching Lifetime Farming Results:', error);
    throw error;
  }
};

/**
 * Gets available companies from PMT3 Lifetime Farming Results collection
 */
export const getAvailableCompaniesForLifetimeFarming = async (): Promise<string[]> => {
  try {
    const collectionRef = collection(db, 'pmt3_lifetime_farming_results');
    const querySnapshot = await getDocs(collectionRef);
    
    const companies: string[] = [];
    querySnapshot.forEach(doc => {
      companies.push(doc.id);
    });
    
    return companies.sort();
  } catch (error) {
    console.error('Error fetching available companies for lifetime farming:', error);
    return [];
  }
};